import { useState, useEffect } from 'react';

const features = [
  {
    id: 'templates',
    title: 'Pick a Template',
    subtitle: 'Start with a pre-built LIMS',
    description: 'Choose from minerals, cannabis, environmental, or food testing templates. Each includes sample tracking, QC workflows, and professional reports.',
    screenshot: '/template_page.png',
    color: {
      gradient: 'from-blue-500 to-blue-600',
      accent: 'bg-blue-500',
      text: 'text-blue-600'
    }
  },
  {
    id: 'data',
    title: 'Customize Your Data',
    subtitle: 'Add fields and tables',
    description: 'Add custom fields like "Geologist Name" or "Project Code". Change field types, add dropdown options. Your data model, your way.',
    screenshot: '/database_page.png',
    color: {
      gradient: 'from-blue-600 to-blue-700',
      accent: 'bg-blue-600',
      text: 'text-blue-700'
    }
  },
  {
    id: 'pages',
    title: 'Build Pages Visually',
    subtitle: 'Drag-and-drop editor',
    description: 'Create dashboards, forms, and data views with our visual editor. No code required. See changes in real-time.',
    screenshot: '/page_editor.png',
    color: {
      gradient: 'from-cyan-500 to-blue-600',
      accent: 'bg-cyan-500',
      text: 'text-cyan-700'
    }
  },
  {
    id: 'workflows',
    title: 'Add Workflows',
    subtitle: 'Automate routine work',
    description: 'When a sample arrives, send emails, update status, run QC checks automatically. Build workflows with our visual editor.',
    screenshot: '/workflow_editor.png',
    color: {
      gradient: 'from-indigo-500 to-blue-600',
      accent: 'bg-indigo-500',
      text: 'text-indigo-700'
    }
  }
];

export function FeaturesTabSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);

  // Auto-advance tabs every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
        setProgress((prev) => {
        if (prev >= 100) {
            return 100;
        }
        return prev + 1;
        });
    }, 100);

    return () => clearInterval(interval);
    }, []);

    // Separate effect to handle tab advancement
    useEffect(() => {
    if (progress >= 100) {
        const timeout = setTimeout(() => {
        setActiveTab((current) => (current + 1) % features.length);
        setProgress(0);
        }, 100);
        
        return () => clearTimeout(timeout);
    }
    }, [progress]);

  // Reset progress when manually changing tabs
  const handleTabClick = (index) => {
    setActiveTab(index);
    setProgress(0);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="mx-auto max-w-[1400px] px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Start with a Template, Customize Everything
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Pick a pre-built LIMS for your lab type. Customize pages, components, workflows, 
            reports and more without code. Go live in days.
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {features.map((feature, index) => (
            <button
              key={feature.id}
              onClick={() => handleTabClick(index)}
              className={`relative p-6 rounded-xl text-left transition-all ${
                activeTab === index
                  ? 'bg-white shadow-xl scale-105 ring-2 ring-blue-500/20'
                  : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
            >
              {/* Top color accent */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 rounded-t-xl bg-gradient-to-r ${
                  feature.color.gradient
                } ${activeTab === index ? 'opacity-100' : 'opacity-0'} transition-opacity`}
              />

              {/* Content */}
              <div className="mt-2">
                <div className="text-lg font-bold mb-2 text-gray-900">
                  {feature.title}
                </div>
                <div className="text-sm text-gray-600">
                  {feature.subtitle}
                </div>
              </div>

              {/* Progress bar (only on active tab) */}
              {activeTab === index && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 rounded-b-xl overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${feature.color.gradient} transition-all`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-gray-200">
          <div className="flex flex-col gap-8">
            {/* Description */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {features[activeTab].title}
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                {features[activeTab].description}
              </p>

              {/* Feature-specific details - 2 columns on desktop */}
              {activeTab === 0 && (
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Minerals & geochemistry testing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Cannabis compliance testing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Environmental (water, soil, air)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Food & beverage testing</span>
                  </li>
                </ul>
              )}

              {activeTab === 1 && (
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Add custom fields (text, number, dropdown, date)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Create new tables for your data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Set validation rules and defaults</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Changes apply everywhere instantly</span>
                  </li>
                </ul>
              )}

              {activeTab === 2 && (
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Drag-and-drop components</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Tables, forms, charts, dashboards</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Customize colors, fonts, spacing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Preview changes in real-time</span>
                  </li>
                </ul>
              )}

              {activeTab === 3 && (
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Email notifications when samples arrive</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Auto-update status when QC passes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Generate and email COAs automatically</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-gray-700">Visual workflow builder (no coding)</span>
                  </li>
                </ul>
              )}
            </div>

            {/* Screenshot - Full Width Below Text */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl ring-1 ring-gray-200">
              <img
                src={features[activeTab].screenshot}
                alt={features[activeTab].title}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}